import React, { useMemo, useState } from 'react';
import {
  Panel, Table, Row, Pill, ColorDot, EmptyState
} from './Timetable.styles';
import {
  formatTeachers, formatEnrolment, studentNames, isCancelled, hasConflict,
  getLessonStatusLabel, getLessonStatusColor, getLessonTypeLabel
} from '../../../api/timetableApi';

/**
 * Flat, sortable table of every occurrence in range — the view for answering
 * "find me this student / teacher / class", and for exporting.
 */

/**
 * One definition per column, used for the header, the cells, the sort and the CSV alike —
 * the table and the export cannot drift out of step.
 *
 * `sort` is the comparable value, `cell` the rendered node, `text` the CSV string. `text`
 * defaults to `sort` where they agree, which is most of them.
 */
const buildColumns = ({ multiCampus, colorOf }) => [
  {
    key: 'date',
    label: 'Date',
    sort: o => o.date,
    cell: o => formatDate(o.date)
  },
  {
    key: 'startTime',
    label: 'Time',
    sort: o => o.startTime,
    cell: o => `${o.startTime}–${o.endTime}`,
    text: o => `${o.startTime}-${o.endTime}`
  },
  // Only when the view spans campuses: otherwise every row would repeat the same name.
  ...(multiCampus ? [{
    key: 'campusName',
    label: 'Campus',
    sort: o => o.campusName || '',
    cell: o => o.campusName || '—',
    text: o => o.campusName || ''
  }] : []),
  {
    key: 'title',
    label: 'Class / Student',
    sort: o => o.title || '',
    cell: o => (
      <>
        <ColorDot $color={colorOf(o)} />
        {o.title || '(untitled)'}
        {o.guestName ? ` · ${o.guestName}` : ''}
      </>
    ),
    text: o => o.title || ''
  },
  {
    key: 'teachers',
    label: 'Teacher',
    sort: o => formatTeachers(o),
    cell: o => formatTeachers(o) || '—',
    text: o => formatTeachers(o)
  },
  {
    key: 'room',
    label: 'Room',
    sort: o => o.room || '',
    cell: o => o.room || '—',
    text: o => o.room || ''
  },
  {
    key: 'typeName',
    label: 'Type',
    sort: o => o.typeName || '',
    cell: o => o.typeName || '—',
    text: o => o.typeName || ''
  },
  {
    key: 'lessonType',
    label: 'Lesson',
    sort: o => o.lessonType || '',
    cell: o => getLessonTypeLabel(o.lessonType),
    text: o => getLessonTypeLabel(o.lessonType)
  },
  {
    key: 'enrolledCount',
    label: 'Students',
    sort: o => o.enrolledCount || 0,
    cell: o => formatEnrolment(o),
    text: o => formatEnrolment(o)
  },
  {
    // The list is the view with room for the roll, and the one that gets exported: a
    // printed week that names who should be in each class is what a campus actually rings
    // round with. Sorted by the first name so the column groups sensibly.
    key: 'studentNames',
    label: 'Enrolled',
    sort: o => (studentNames(o)[0] || ''),
    cell: o => studentNames(o).join(', ') || o.guestName || '—',
    text: o => studentNames(o).join(', ') || o.guestName || ''
  },
  {
    key: 'status',
    label: 'Status',
    sort: o => o.status || '',
    cell: o => (
      <Pill $color={getLessonStatusColor(o.status)}>{getLessonStatusLabel(o.status)}</Pill>
    ),
    text: o => getLessonStatusLabel(o.status)
  },
  {
    key: 'conflicts',
    label: 'Clash',
    sort: o => (o.conflicts || []).join(','),
    cell: o => (hasConflict(o)
      ? <Pill $color="#ef4444">{o.conflicts.join(' + ')}</Pill>
      : '—'),
    text: o => (o.conflicts || []).join(' + ')
  }
];

const formatDate = (dateStr) => {
  const date = new Date(`${dateStr}T00:00:00`);
  return date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
};

const ListView = ({ occurrences, colorOf, onSelect, multiCampus = false }) => {
  const [sortKey, setSortKey] = useState('date');
  const [ascending, setAscending] = useState(true);

  const columns = useMemo(
    () => buildColumns({ multiCampus, colorOf }),
    [multiCampus, colorOf]
  );

  const sorted = useMemo(() => {
    const column = columns.find(c => c.key === sortKey) || columns[0];
    const rows = [...occurrences];
    rows.sort((a, b) => {
      const av = column.sort(a);
      const bv = column.sort(b);
      let result;
      if (typeof av === 'number' && typeof bv === 'number') {
        result = av - bv;
      } else {
        result = String(av).localeCompare(String(bv), undefined, { numeric: true });
      }
      // Date is the natural secondary key: rows sorted by teacher still read chronologically
      if (result === 0 && column.key !== 'date') {
        result = `${a.date}${a.startTime}`.localeCompare(`${b.date}${b.startTime}`);
      }
      return ascending ? result : -result;
    });
    return rows;
  }, [occurrences, columns, sortKey, ascending]);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setAscending(prev => !prev);
    } else {
      setSortKey(key);
      setAscending(true);
    }
  };

  if (occurrences.length === 0) {
    return (
      <EmptyState>
        <h3>No classes match</h3>
        <p>Try widening the date range or clearing a filter.</p>
      </EmptyState>
    );
  }

  return (
    <Panel>
      <Table>
        <thead>
          <tr>
            {columns.map(column => (
              <th key={column.key} onClick={() => toggleSort(column.key)}>
                {column.label}
                {sortKey === column.key ? (ascending ? ' ▲' : ' ▼') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map(occurrence => (
            <Row
              key={occurrence.id}
              $cancelled={isCancelled(occurrence)}
              onClick={() => onSelect(occurrence)}
            >
              {columns.map(column => (
                // data-label carries the header into the phone layout, where the table folds
                // into one card per class and each cell prints its own label.
                <td key={column.key} data-label={column.label}>{column.cell(occurrence)}</td>
              ))}
            </Row>
          ))}
        </tbody>
      </Table>
    </Panel>
  );
};

/**
 * CSV of the rows currently on screen, so an export always matches what the filters show.
 */
export const toCsv = (occurrences, multiCampus = false) => {
  const columns = buildColumns({ multiCampus, colorOf: () => '' });
  const header = columns.map(c => c.label);
  const rows = occurrences.map(occurrence =>
    columns.map(column => (column.text || column.sort)(occurrence))
  );

  return [header, ...rows]
    .map(row => row.map(cell => {
      const value = String(cell ?? '');
      return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
    }).join(','))
    .join('\n');
};

export default ListView;
